import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { concatMap, takeUntil } from 'rxjs/operators';
import { sortBySeverityColors } from 'src/app/components/charts/charts.class';
import { LS } from 'src/app/helpers/localStorage.enum';
import { DataService } from 'src/app/services/data/data.service';
import { LocationUIService } from 'src/app/services/location-ui/location-ui.service';
import { GenericTypeService } from 'src/app/services/generic-type/generic-type.service';
import { SplitViewService } from 'src/app/services/split-view/split-view.service';
import { LayerStoreService } from 'src/app/stores/layer-store/layer-store.service';
import { LocationStoreService } from 'src/app/stores/location-store/location-store.service';
import { AnnotationStoreService } from 'src/app/stores/annotation-store/annotation-store.service';
import { SwalService } from 'src/app/services/swal/swal.service';
import { AnnotationService } from 'src/app/services/annotation/annotation.service';

@Component({
  selector: 'app-list-annotation',
  templateUrl: './list-annotation.component.html',
  styleUrls: ['./list-annotation.component.sass'],
})
export class ListAnnotationComponent implements OnInit, OnDestroy {
  id;
  _annotations = [];
  _fullannotations = [];
  annotations = [];
  mapServiceSub: Subscription;
  bulkUpdateStatusSub: Subscription;
  currLoc;

  severityGroup: FormGroup;

  severityList: any[];

  unsub$ = new Subject();

  ui = {
    isMultiFilter: false,
    // isFiltered: false
  };
  selectedAnnotation: string[] = [];
  isAllCheckboxIndeterminate: boolean = false;
  hasPending: boolean;
  hasDraft: boolean;
  currentUser: any;

  get isFiltered() {
    const checkboxes: ISCheckboxes = this.severityGroup?.value;
    return checkboxes
      ? Object.keys(checkboxes).some((key) => !checkboxes[key])
      : false;
  }

  permissions: any;
  isSplitscreenOn = false;

  /**
   * Toggle list of annotations when splitscreen is on
   * If true: List annotation will display what's on active map only
   * If false: List annotation display all annotations
   */
  splitSwitch: boolean = false;

  constructor(
    private layerStore: LayerStoreService,
    private dataService: DataService,
    private formBuilder: FormBuilder,
    private gTypeService: GenericTypeService,
    private locationUiService: LocationUIService,
    private route: ActivatedRoute,
    private router: Router,
    private splitViewService: SplitViewService,
    private locationStore: LocationStoreService,
    private annotationStore: AnnotationStoreService,
    private annotationService: AnnotationService,
    private swalService: SwalService
  ) {}

  ngOnInit(): void {
    this.currentUser = JSON.parse(localStorage.getItem(LS.currentUser));
    this.permissions = this.currentUser.permissions;
    this.severityGroup = this.formBuilder.group({});

    this.locationStore.currLocation$
      .pipe(takeUntil(this.unsub$.asObservable()))
      .subscribe((res) => {
        if (res.id) {
          this.currLoc = res;
          this.getSeverityList();
          this.annotationStore.annotations$
            .pipe(takeUntil(this.unsub$.asObservable()))
            .subscribe((resAnnot) => {
              this._fullannotations =
                this._annotations =
                this.annotations =
                  resAnnot;

              this.hasPending =
                resAnnot.filter((f) => f.review_status === 'pending').length >
                  0 &&
                res.reviewers.filter((r) => r.id === this.currentUser.user_id)
                  .length > 0 &&
                this.currLoc.endorsement_status === 'draft';

              this.hasDraft =
                resAnnot.filter((f) => f.review_status === 'draft').length >
                  0 &&
                res.submitter.filter((r) => r.id === this.currentUser.user_id)
                  .length > 0 &&
                this.currLoc.endorsement_status === 'draft';

              this.isSplitscreenOn = this.splitViewService.isSplitscreenOn;
              this.initSplitSwitch();
            });
        }
      });

    this.splitViewService.onEvent$
      .pipe(takeUntil(this.unsub$.asObservable()))
      .subscribe((res) => {
        if (res.isSplitscreenOn !== undefined) {
          this.isSplitscreenOn = res.isSplitscreenOn;
          this.initSplitSwitch();

          if (this.isFiltered) this.resetFilter();
        }
        if (res.onActiveMapChange && 'map' in res.onActiveMapChange) {
          this.onSplitSwitchToggle(this.splitSwitch);

          if (
            Object.keys(
              this.splitViewService?.selectedMapView?.severityFilter || {}
            ).length
          ) {
            // this.ui.isFiltered = true
            this.severityToggleMethod(
              this.splitViewService.selectedMapView.severityFilter
            );
          } else {
            if (this.isFiltered) this.resetFilter();
            if (this.splitSwitch)
              this.filterAnnotations('map_layer.id', [
                res?.onActiveMapChange?.map_layer?.id,
              ]);
          }
        }
        if (res.onAddOrtho) {
          if (this.isFiltered) this.resetFilter();
          if (this.splitSwitch)
            this.filterAnnotations('map_layer.id', [res?.orthoLayer?.id]);
        }
      });
  }

  onDeleted(annotation_key) {
    this.locationUiService.refreshCurrLocation(this.currLoc.id);
    this._annotations.splice(annotation_key, 1);
    this.annotations = this._annotations;
  }

  ngOnDestroy() {
    this.unsub$.next();
    this.unsub$.unsubscribe();
    if (this.mapServiceSub) this.mapServiceSub.unsubscribe();
  }

  patchSeverityFilter(checkBoxes: ISCheckboxes) {
    this.severityGroup.patchValue(checkBoxes, { emitEvent: false });
  }

  getSeverityList(): void {
    let severityList = [];

    this.layerStore.severityFilter$
      .pipe(
        takeUntil(this.unsub$.asObservable()),
        concatMap((filtered: string[]) => {
          if (filtered?.length && this.severityList && this.severityGroup) {
            let patch = {};
            this.severityList.map((sever) => {
              const id = sever.id,
                isFiltered = filtered.includes(id);

              patch[id] = isFiltered;
            });
            this.patchSeverityFilter(patch);

            if (this.isSplitscreenOn)
              this.splitViewService.selectedMapView.severityFilter = patch;

            Object.keys(patch).map((id) => {
              const stat = patch[id];
              if (stat) this.filterAnnotations('severity_type.id', [id]);
            });
          }

          return this.gTypeService.findAll([
            { key: 'filtered[0][key]', val: 'category' },
            { key: 'filtered[0][val]', val: 'severity' },
          ]);
        }),
        concatMap((res: { data: any[] }) => {
          severityList = res.data;

          return this.dataService.defectCount$;
        })
      )
      .subscribe(() => {
        const severity_type =
          this.locationStore.allLocations[this.currLoc.id].defectSeverities;

        const unsorted = severityList.filter((sever) =>
          (severity_type as any[]).find((sType) => sType.id === sever.id)
        );
        this.severityList = sortBySeverityColors(unsorted, 'name', true);
        this.initSeverFilter();
      });
  }

  initSeverFilter() {
    this.severityList.map((sever) => {
      this.severityGroup.addControl(`${sever.id}`, new FormControl(true));
    });

    this.severityGroup.valueChanges
      .pipe(takeUntil(this.unsub$.asObservable()))
      .subscribe(this.onMultiFilterToggled);
  }

  onMultiFilterToggled = (checkBoxes: ISCheckboxes) => {
    if (this.ui.isMultiFilter) {
      this.splitViewService.selectedMapView.severityFilter = checkBoxes;

      this.severityToggleMethod(checkBoxes);
    }
  };

  severityToggleMethod(checkbox: ISCheckboxes) {
    Object.keys(checkbox).map((id) => {
      const stat = checkbox[id];

      if (stat)
        this.layerStore.toggleAnnotationBy({
          category: 'severity_type',
          id,
          isInverse: false,
          force: true,
          skipZoom: this.isSplitscreenOn,
        });
      else
        this.layerStore.toggleAnnotationBy({
          category: 'severity_type',
          id,
          isInverse: true,
          force: true,
          skipZoom: this.isSplitscreenOn,
        });
    });
  }

  /**
   * Event handler for when severity filter is clicked
   * @param severityId Severity IDs
   * @param evt Click event
   */
  severFilterClicked(severityId: string, evt: any) {
    if (this.ui.isMultiFilter || !severityId) return;

    setTimeout(() => {
      // if (!this.ui.isFiltered) this.ui.isFiltered = true
      this.layerStore.toggleAnnotationBy({
        category: 'severity_type',
        id: severityId,
        isInverse: true,
        force: false,
        skipZoom: this.isSplitscreenOn,
      });
      // this.layerStore.toggleAnnotationBy('severity_type', severityId, true, false)
      this.filterAnnotations('severity_type.id', [severityId]);
    }, 50);
  }

  resetFilter() {
    // this.ui.isFiltered = false

    if (this.isSplitscreenOn)
      this.splitViewService.selectedMapView.severityFilter = null;

    this.severityList.map((sever) => {
      this.layerStore.toggleAnnotationBy({
        category: 'severity_type',
        id: sever.id,
        isInverse: false,
        force: true,
        skipZoom: this.isSplitscreenOn,
      });
      // this.layerStore.toggleAnnotationBy('severity_type', sever.id, false, true)
      // this.annotations = this._annotations
    });
  }

  onClickCreate() {
    this.router.navigate(['create'], { relativeTo: this.route });
  }

  onToggleChange(name: string, evt: any) {
    switch (name) {
      case 'splitSwitch': {
        this.splitSwitch = !this.splitSwitch;
        this.onSplitSwitchToggle(this.splitSwitch);
        break;
      }
      default: {
      }
    }
  }

  initSplitSwitch() {
    this.splitSwitch = this.isSplitscreenOn;
    // if (this.splitSwitch) this._annotations = this._fullannotations.filter(this.)
    // const currMapLayer = this.splitViewService.selectedOrtho
    // if (currMapLayer)
    //   this._annotations = this._fullannotations.filter(annot => annot?.map_layer?.id === currMapLayer?.id)

    this.onSplitSwitchToggle(this.splitSwitch);
  }

  onSplitSwitchToggle(value: boolean) {
    if (value) {
      const currOrtho =
        this.splitViewService.mapViews[
          this.splitViewService.selectedMapViewIndex
        ].ortho;

      if (!currOrtho?.id) {
        this.annotations = [];
        return;
      }

      this.filterAnnotations('map_layer.id', [currOrtho.id]);
      return;
    }

    this.annotations = this._annotations = this._fullannotations;
  }

  filterAnnotations(field: string, filterIds: string[]) {
    this.annotations = this._annotations = this._fullannotations.filter(
      (annot) => {
        const val = _get(annot, field, null);

        return filterIds.indexOf(val) > -1;
      }
    );
  }

  selectAllAnnotation(event) {
    this.isAllCheckboxIndeterminate = false;
    if (event.target.checked) {
      this.selectedAnnotation = this.annotations
        .filter((f) => f.review_status === 'pending')
        .map((i) => i.id);
    } else {
      this.selectedAnnotation = [];
    }
  }

  selectAnnotation(event, id) {
    event.preventDefault();
    this.isAllCheckboxIndeterminate = true;
    if (event.target.checked) {
      this.selectedAnnotation.push(id);
    } else {
      this.selectedAnnotation.splice(this.selectedAnnotation.indexOf(id), 1);
      if (this.selectedAnnotation.length === 0) {
        this.isAllCheckboxIndeterminate = false;
      }
    }
  }

  bulkUpdateReviewStatus(status) {
    switch (status) {
      case 'reject':
        this.swalService.confirmDialog(
          (swalRes) => {
            const { value } = swalRes.result;
            if (value.trim().length > 0) {
              this.bulkUpdateStatusSub = this.annotationService
                .bulkUpdateReviewStatus({
                  annotation_ids: this.selectedAnnotation,
                  review_status: status,
                  review_remarks: value,
                })
                .subscribe(async (res) => {
                  swalRes.resultFunc();
                  console.log(res);
                  this.isAllCheckboxIndeterminate = false;
                  this.selectedAnnotation = [];
                  this.locationUiService.refreshCurrLocation(this.currLoc.id);
                });
            }
          },
          {
            text: 'To proceed update in bulk, please enter your remarks.',
            input: 'textarea',
            preConfirmValidator: (remarks) => {
              if (remarks.trim().length === 0) {
                return { error: true, message: 'Remarks cannot be empty' };
              }
              return { error: false, message: '' };
            },
            confirmButtonText: 'Yes, reject selected!',
            customConfirmedAttr: {
              title: 'Updated!',
              content: 'Selected annotation review status have been updated.',
            },
          }
        );
        break;

      default:
        if (status === 'pending') {
          this.selectedAnnotation = this.annotations.map((i) => i.id);
        }
        this.swalService.confirmDialog(
          (swalRes) => {
            this.bulkUpdateStatusSub = this.annotationService
              .bulkUpdateReviewStatus({
                annotation_ids: this.selectedAnnotation,
                review_status: status,
                review_remarks: '',
              })
              .subscribe(async (res) => {
                swalRes();
                console.log(res);
                this.isAllCheckboxIndeterminate = false;
                this.selectedAnnotation = [];
                this.locationUiService.refreshCurrLocation(this.currLoc.id);
              });
          },
          {
            text: '',
            confirmButtonText: 'Yes, update review status!',
            customConfirmedAttr: {
              title: 'Updated!',
              content: 'Selected annotation review status have been updated.',
            },
          }
        );
        break;
    }
  }
}

/**
 * Retrieves nested value from param Value, stored according to param Path
 * @param {*} value
 * @param {string} path
 * @param {*} defaultValue
 * Stolen from https://gist.github.com/jeneg/9767afdcca45601ea44930ea03e0febf#gistcomment-3159161
 */
export const _get = (value: any, path: string, defaultValue: any) =>
  String(path)
    .split('.')
    .reduce((acc, v) => {
      try {
        acc = acc[v] === undefined ? defaultValue : acc[v];
      } catch (e) {
        return defaultValue;
      }
      return acc;
    }, value);

/** Interface for severity checkboxes */
interface ISCheckboxes {
  [severityId: string]: boolean;
}
